// GlobalDataVisualizationModal.tsx
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useStore } from "@/store";
import GenericDataVisualizationModal from "./GenericDataVisualizationModal";
import { useDataVisualizationConfig } from "./dataVisualizationRegistry";
import { useNavigate } from "@tanstack/react-router";
import { ColumnDef } from "@tanstack/react-table";
import { ComponentType } from "react";
import {
  TColumnProviderProps,
  TQueryHook,
} from "@/core/types/generic-data-visualization";
import { CommonModalUI } from "@/components/ui/Modal";

/**
 * Global Data Visualization Modal Component
 * Fully generic - uses ColumnProvider components from registry with lazy loading
 */
const GlobalDataVisualizationModal = () => {
  const navigate = useNavigate();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [isLoadingDependencies, setIsLoadingDependencies] = useState(false);
  const [loadedQueryHook, setLoadedQueryHook] = useState<TQueryHook<
    any,
    any,
    any
  > | null>(null);
  const [LoadedColumnProvider, setLoadedColumnProvider] =
    useState<ComponentType<TColumnProviderProps> | null>(null);

  const modalState = useStore((state) => state.isOpenDataVisulizationModal);
  const setIsOpenDataVisulizationModal = useStore(
    (state) => state.setIsOpenDataVisulizationModal,
  );

  const config = useDataVisualizationConfig(modalState?.configType ?? "");

  // ✅ Load dependencies when modal opens
  useEffect(() => {
    if (!modalState?.open || !config) {
      // Reset when modal closes
      setLoadedQueryHook(null);
      setLoadedColumnProvider(null);
      return;
    }

    // Load the query hook and column provider
    setIsLoadingDependencies(true);
    Promise.all([config.loadQueryHook(), config.loadColumnProvider()])
      .then(([queryHook, ColumnProvider]) => {
        setLoadedQueryHook(() => queryHook);
        setLoadedColumnProvider(() => ColumnProvider);
      })
      .catch((error) => {
        console.error("Failed to load modal dependencies:", error);
      })
      .finally(() => {
        setIsLoadingDependencies(false);
      });
  }, [modalState?.open, config]);

  // ✅ Create row click handler
  const handleRowClick = useCallback(
    (id: string) => {
      if (!config?.createRowClickHandler) return;
      startTransition(() => {
        void config.createRowClickHandler?.(
          {
            navigate,
            modalParams: modalState?.params,
            setLoadingId,
          },
          id,
        );
      });
    },
    [config, navigate, modalState?.params],
  );

  // ✅ Prepare context
  const columnContext = useMemo(
    () => ({
      onRowClick: config?.createRowClickHandler ? handleRowClick : undefined,
      loadingId,
    }),
    [config?.createRowClickHandler, handleRowClick, loadingId],
  );

  // ✅ Don't render if modal is closed or config not found
  if (!modalState?.open || !config) {
    return null;
  }

  // ✅ Show loading state while dependencies are being loaded
  if (isLoadingDependencies || !loadedQueryHook || !LoadedColumnProvider) {
    return (
      <CommonModalUI
        open={modalState.open}
        onClose={() =>
          setIsOpenDataVisulizationModal({
            ...modalState,
            open: false,
          })
        }
        title={modalState.title ?? config.title}
        size={config.size ?? "md"}
        type="default"
        id={`global-data-visualization-loading-${modalState.configType}`}
      >
        <div className="flex items-center justify-center gap-3 py-6">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
          <span>Loading...</span>
        </div>
      </CommonModalUI>
    );
  }

  // ✅ Now render with loaded dependencies
  return (
    <LoadedColumnProvider context={columnContext}>
      {(columns: ColumnDef<any>[]) => (
        <GenericDataVisualizationModal
          isOpen={modalState.open}
          onClose={() =>
            setIsOpenDataVisulizationModal({
              ...modalState,
              open: false,
            })
          }
          title={modalState.title ?? config.title}
          size={config.size ?? "md"}
          useQueryHook={loadedQueryHook}
          queryParams={modalState.params ?? {}}
          columns={columns}
          transformData={config.transformData}
          dataExtractor={config.dataExtractor}
          countExtractor={config.countExtractor}
          initialColumnPinning={config.initialColumnPinning}
          queryOptions={config.queryOptions}
          tableId={`global-data-visualization-${modalState.configType}`}
          paginationId={`global-data-visualization-pagination-${modalState.configType}`}
        />
      )}
    </LoadedColumnProvider>
  );
};

export default GlobalDataVisualizationModal;
